import { OnDestroy, OnInit } from '@angular/core';
import { Component } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { LayoutService } from './service/app.layout.service';

/** Keeps the icon markup readable next to the labels it belongs to. */
const icon = (name: string) => `<span class="material-symbols-outlined text-[16px]">${name}</span>`;

/**
 * Routes that carry query parameters or child segments need the active state to
 * survive them, otherwise the entry stops looking selected as soon as a filter
 * or a detail view is opened.
 */
const KEEP_ACTIVE_ON_CHILDREN = {
  paths: 'subset',
  queryParams: 'ignored',
  matrixParams: 'ignored',
  fragment: 'ignored',
};

@Component({
  selector: 'bo-menu',
  templateUrl: './app.menu.component.html',
})
export class AppMenuComponent implements OnInit, OnDestroy {
  model: any[] = [];

  private languageSubscription?: Subscription;

  constructor(
    public layoutService: LayoutService,
    private readonly translate: TranslateService,
  ) {}

  ngOnInit() {
    this.buildModel();
    // Labels are plain interpolations in the item template, so the model is
    // rebuilt on a language switch rather than making every entry reactive.
    this.languageSubscription = this.translate.onLangChange.subscribe(() => this.buildModel());
  }

  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
  }

  private buildModel() {
    const t = (key: string) => this.translate.instant(key);
    // Grouped by business domain rather than by page type: someone looking into a
    // payment question should find every payment screen in one place. Every route
    // and label from the previous layout is kept, only the grouping changed.
    this.model = [
      {
        label: t('menu.steering'),
        items: [
          {
            label: t('menu.synthesis'),
            icon: icon('insights'),
            routerLink: ['/synthese'],
          },
          {
            label: t('menu.todo'),
            icon: icon('task_alt'),
            routerLink: ['/a-traiter'],
          },
          {
            label: t('menu.studio'),
            icon: icon('campaign'),
            routerLink: ['/studio'],
          },
          {
            label: t('menu.userActivity'),
            icon: icon('activity_zone'),
            routerLink: ['/user-activity'],
          },
          {
            label: t('menu.userAnalytics'),
            icon: icon('bar_chart'),
            routerLink: ['/report'],
          },
          {
            label: t('menu.segments'),
            icon: icon('groups_3'),
            routerLink: ['/segments'],
          },
          {
            label: t('menu.fillRate'),
            icon: icon('donut_large'),
            routerLink: ['/remplissage'],
          },
          {
            label: t('menu.acquisition'),
            icon: icon('person_add'),
            routerLink: ['/acquisition'],
          },
          {
            label: t('menu.activation'),
            icon: icon('how_to_reg'),
            routerLink: ['/activation'],
          },
          {
            label: t('menu.matching'),
            icon: icon('hub'),
            routerLink: ['/appariement'],
          },
          {
            label: t('menu.needingHelp'),
            icon: icon('detector_alarm'),
            routerLink: ['/activites-en-difficulte'],
          },
          {
            label: t('menu.heatmap'),
            icon: icon('map'),
            routerLink: ['/carte-de-chaleur'],
          },
          {
            label: t('menu.stores'),
            icon: icon('smartphone'),
            routerLink: ['/stores'],
          },
          {
            label: t('menu.crossAnalysis'),
            icon: icon('grid_view'),
            routerLink: ['/analyse-croisee'],
          },
          {
            label: t('menu.citiesToActivate'),
            icon: icon('location_city'),
            routerLink: ['/villes-a-activer'],
          },
          {
            label: t('menu.duplicateCities'),
            icon: icon('pin_drop'),
            routerLink: ['/villes-en-doublon'],
          },
          {
            label: t('menu.eventRevenue'),
            icon: icon('chart_data'),
            routerLink: ['/event-revenue'],
          },
        ],
      },
      {
        label: t('menu.users'),
        items: [
          {
            label: t('menu.userLists'),
            icon: icon('id_card'),
            routerLink: ['/users'],
          },
          {
            label: t('menu.usersManagement'),
            icon: icon('manage_accounts'),
            routerLink: ['/users-management'],
          },
          {
            label: t('menu.groups'),
            icon: icon('groups'),
            routerLink: ['/groups'],
          },
        ],
      },
      {
        label: t('menu.activities'),
        items: [
          {
            label: t('menu.futureActivities'),
            icon: icon('calendar_add_on'),
            routerLink: ['/events'],
          },
          {
            label: t('menu.pastActivities'),
            icon: icon('event_available'),
            routerLink: ['/past-events'],
          },
          {
            label: t('menu.activitiesManagement'),
            icon: icon('calendar_month'),
            routerLink: ['/activities'],
          },
          {
            label: t('menu.paidEventAccess'),
            icon: icon('key'),
            routerLink: ['/paid-events'],
          },
        ],
      },
      {
        label: t('menu.payments'),
        items: [
          {
            label: t('menu.transactions'),
            icon: icon('directory_sync'),
            routerLink: ['/transactions'],
            routerLinkActiveOptions: KEEP_ACTIVE_ON_CHILDREN,
          },
          {
            label: t('menu.withdrawalRequest'),
            icon: icon('output'),
            routerLink: ['/payout-request'],
          },
          {
            label: t('menu.organizerBalances'),
            icon: icon('account_balance_wallet'),
            routerLink: ['/organizer-balances'],
            routerLinkActiveOptions: KEEP_ACTIVE_ON_CHILDREN,
          },
          {
            label: t('menu.earningsStatement'),
            icon: icon('receipt_long'),
            routerLink: ['/earnings-statement'],
          },
          {
            label: t('menu.commissionsRate'),
            icon: icon('percent'),
            routerLink: ['/commission'],
          },
        ],
      },
      {
        label: t('menu.compliance'),
        items: [
          {
            label: t('menu.termsContent'),
            icon: icon('contract'),
            routerLink: ['/legal'],
          },
          {
            label: t('menu.termsAcceptances'),
            icon: icon('gavel'),
            routerLink: ['/cgv-acceptance'],
          },
          {
            label: t('menu.auditLogs'),
            icon: icon('policy'),
            routerLink: ['/audit-logs'],
          },
        ],
      },
      {
        label: t('menu.administration'),
        items: [
          {
            label: t('menu.deletedItems'),
            icon: icon('delete'),
            routerLink: ['/deleted-items'],
          },
        ],
      },
    ];
  }
}
